import { useMemo } from 'react';
import { Alert, Loader, Stack, Text, Title } from '@mantine/core';
import { useIntl } from 'react-intl';

import { ratesPanelMessages } from './rates-panel-messages';
import styles from './RatesPanel.module.css';
import { FREIGHT_QUOTE_ERROR_CODE } from '../../../../ltl/constants';
import { FreightNoCarriersMessage } from '../../../freight-no-carriers-message/freight-no-carriers-message';
import { FreightRateCard } from '../../../freight-rate-card/FreightRateCard';
import { groupRates } from '../../../freight-rate-card/rate-grouping';

import type { LtlAddress, LtlQuote } from '../../../../types/ltl';
import type { ShipFromAddress } from '../../../../types/ship-from';
import type { ReactNode } from 'react';

type RatesPanelProps = {
  rates: LtlQuote[];
  isLoading: boolean;
  isInsured: boolean;
  rateError?: ReactNode;
  rateErrorCode?: string | null;
  connectedFreightProviderId?: number | null;
  selectedOfferId?: string;
  onSelectQuote: (quote: LtlQuote) => void;
  shipFromAddress: ShipFromAddress;
  destinationAddress: LtlAddress;
};

export const RatesPanel = ({
  rates,
  isLoading,
  isInsured,
  rateError,
  rateErrorCode,
  connectedFreightProviderId,
  selectedOfferId,
  onSelectQuote,
  shipFromAddress,
  destinationAddress,
}: RatesPanelProps) => {
  const intl = useIntl();

  const grouped = useMemo(() => groupRates(rates), [rates]);
  const carrierRows = isInsured ? grouped.withInsurance : grouped.withoutInsurance;
  const isQuoteIneligible = rateErrorCode === FREIGHT_QUOTE_ERROR_CODE.QuoteIneligible;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Title order={4}>{intl.formatMessage(ratesPanelMessages.title)}</Title>
        {carrierRows.length > 0 && (
          <Text c='dimmed' size='sm'>
            {intl.formatMessage(ratesPanelMessages.carriersAvailable, { count: carrierRows.length })}
          </Text>
        )}
      </div>
      <Stack className={styles.body} gap='md'>
        {rateError && !isQuoteIneligible && (
          <Alert color='red' data-testid='rate-error'>
            {rateError}
          </Alert>
        )}
        {isLoading && (
          <div className={styles.loaderWrapper}>
            <Loader data-testid='rates-loader' />
          </div>
        )}
        {!isLoading && isQuoteIneligible && carrierRows.length === 0 && (
          <div className={styles.emptyState}>
            <FreightNoCarriersMessage freightProviderId={connectedFreightProviderId} />
          </div>
        )}
        {!isLoading && rates.length === 0 && !rateError && !isQuoteIneligible && (
          <Text className={styles.emptyState} c='dimmed' data-testid='rates-empty'>
            {intl.formatMessage(ratesPanelMessages.noRates)}
          </Text>
        )}
        {!isLoading && carrierRows.length > 0 && (
          <div className={styles.ratesList}>
            {carrierRows.map((carrierRow) => (
              <FreightRateCard
                carrierRow={carrierRow}
                destinationAddress={destinationAddress}
                key={carrierRow.carrierKey}
                layout='horizontal'
                onSelectRate={onSelectQuote}
                selectedOfferId={selectedOfferId}
                shipFromAddress={shipFromAddress}
              />
            ))}
          </div>
        )}
      </Stack>
    </div>
  );
};
